import React from "react";
import { Image } from "antd";

import type { WhatsAppMedia } from "./types";

interface WhatsAppMessageMediaProps {
  media: WhatsAppMedia;
  messageType: string;
}

const MEDIA_LABELS: Record<string, string> = {
  audio: "Voice message",
  voice: "Voice message",
  image: "Photo",
  sticker: "Sticker",
  video: "Video",
  document: "Document",
};

const FILE_ICONS: Record<string, string> = {
  "application/pdf": "ti ti-file-type-pdf",
  "application/msword": "ti ti-file-type-doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "ti ti-file-type-doc",
  "application/vnd.ms-excel": "ti ti-file-spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "ti ti-file-spreadsheet",
};

const isSafeMediaUrl = (url: string | null): url is string =>
  typeof url === "string" && url.startsWith("https://");

const getMediaKind = (messageType: string, mimeType: string | null) => {
  const type = messageType.toLowerCase();
  if (["audio", "voice", "image", "sticker", "video", "document"].includes(type)) return type;
  const mime = (mimeType || "").toLowerCase();
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "document";
};

const getFileName = (media: WhatsAppMedia, label: string) => {
  if (media.filename) return media.filename;
  const lastSegment = media.url?.split("?")[0].split("/").pop();
  return lastSegment ? decodeURIComponent(lastSegment) : label;
};

const UnavailableMedia: React.FC<{ label: string }> = ({ label }) => (
  <div className="wa-media wa-media--unavailable">
    <i className="ti ti-file-off me-1" />
    {label} (file not available)
  </div>
);

export const WhatsAppMessageMedia: React.FC<WhatsAppMessageMediaProps> = ({
  media,
  messageType,
}) => {
  const kind = getMediaKind(messageType, media.mime_type);
  const label = MEDIA_LABELS[kind] || "File";

  if (!isSafeMediaUrl(media.url)) return <UnavailableMedia label={label} />;

  if (kind === "audio" || kind === "voice") {
    return (
      <div className="wa-media wa-media--audio">
        <audio controls preload="metadata" src={media.url}>
          <a href={media.url} target="_blank" rel="noopener noreferrer">
            Download {label.toLowerCase()}
          </a>
        </audio>
      </div>
    );
  }

  if (kind === "image" || kind === "sticker") {
    return (
      <div className="wa-media wa-media--image">
        <Image
          src={media.url}
          alt={label}
          width={kind === "sticker" ? 120 : 220}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className="wa-media wa-media--video">
        <video controls preload="metadata" src={media.url} />
      </div>
    );
  }

  const fileName = getFileName(media, label);
  return (
    <a
      className="wa-media wa-media--document"
      href={media.url}
      target="_blank"
      rel="noopener noreferrer"
      download={fileName}>
      <i className={`${FILE_ICONS[media.mime_type || ""] || "ti ti-file"} wa-media__file-icon`} />
      <span className="wa-media__file-name text-truncate">{fileName}</span>
      <i className="ti ti-download" />
    </a>
  );
};
